import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@economic/taco';
import { useLocalization, t } from '../../../../../shared/components/l10n/l10n';
import { usePostQuery } from '../../../../../shared/hooks/usePostQuery';
import { SendInvoicesDialog } from './SendInvoicesDialog';
import {
    BulkSendFromArchiveInformationResponse,
    BulkSendFromArchiveRequest,
    BulkSendFromArchiveResponse,
    SendInvoicesFormData,
} from '../types';
import { SendByType } from '../../invoices/book-and-send/types';
import useMutation from '../../../../../shared/hooks/useMutation';
import { splitEmails } from '../../invoices/utils';
import { refreshCurrentLegacyPage } from '@economic/react-app';

const bulkSendFromArchive = useMutation<BulkSendFromArchiveRequest, BulkSendFromArchiveResponse>(
    `sales/invoice/BulkSendFromArchive`
);

function SendInvoicesDialogContainer() {
    const location = useLocation();
    const invoiceIds = (location.state as { invoiceIds: number[] })?.invoiceIds || [];

    const navigate = useNavigate();

    const { loading: informationLoading, data: information } = usePostQuery<BulkSendFromArchiveInformationResponse>(
        `sales/invoice/BulkSendFromArchiveInformation`,
        {
            invoiceIds,
        }
    );

    const loadingTexts = useLocalization(textIds);
    const toast = useToast();

    const [sending, setSending] = useState<boolean>(false);

    const initialData: SendInvoicesFormData | undefined = useMemo(() => {
        if (!information) {
            return undefined;
        }

        const canSendElectronically = information.sendElectronicInvoicesInfo.filter(x => x.canSend).length > 0;

        return {
            canSendElectronically,
            sendByType: canSendElectronically ? SendByType.oioxml : SendByType.email,
            emailOnly: information.sendElectronicInvoicesInfo.filter(i => !i.canSend).map(i => i.invoiceId),
            emailOptions: {
                ...information,
                saveSubject: false,
                saveBody: false,
                recipients: '',
                recipientsCc: '',
                recipientsBcc: '',
            },
        };
    }, [information]);

    async function send(values: any) {
        if (!information) {
            return;
        }

        setSending(true);

        const documentIds =
            values.sendByType === SendByType.oioxml
                ? information.sendElectronicInvoicesInfo.filter(i => i.canSend).map(i => i.invoiceId)
                : invoiceIds;

        const data = {
            documentIds,
            sendByType: values.sendByType,
            ...values.emailOptions,
            recipients: splitEmails(values.emailOptions.recipients || ''),
            recipientsCc: splitEmails(values.emailOptions.recipientsCc || ''),
            recipientsBcc: splitEmails(values.emailOptions.recipientsBcc || ''),
        };

        const { data: result } = await bulkSendFromArchive(data);
        if (result.failedToSendCount > 0) {
            toast.warning(
                t(`TOAST_INVOICE_SEND_PLURAL_ERROR`, {
                    COUNT: result.failedToSendCount,
                })
            );
        } else {
            toast.success(t`TOAST_INVOICE_SEND_PLURAL_SUCCESS`);
        }

        setSending(false);
        refreshContentFrameAfterSuccess();
        hide();
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname);
    }

    return (
        <SendInvoicesDialog
            loading={loadingTexts || informationLoading || sending}
            initialData={initialData}
            open={location.hash === '#send-invoices'}
            onHide={hide}
            onSend={send}
        />
    );
}

const textIds = [
    'FORM_ARCHIVESEND_INVOICES_HEADER',
    'FORM_ARCHIVESEND_CANNOT_SEND_VIA_EMAIL',

    'FORM_ARCHIVESEND_SEND_BY_EMAIL',
    'FORM_ARCHIVESEND_SEND_BY_EINVOICE',
    'FORM_ARCHIVESEND_BUTTON_CANCEL',
    'FORM_ARCHIVESEND_BUTTON_SEND',
    'TOAST_INVOICE_SEND_PLURAL_SUCCESS',
    'FORM_ARCHIVESEND_ERRORS_CANNOTSEND',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'SHARED_FORM_OK',
    'TOAST_INVOICE_SEND_PLURAL_ERROR',

    'LOADING_TEXT1',
    'LOADING_TEXT2',
    'LOADING_TEXT3',

    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
];

export default SendInvoicesDialogContainer;
